import React, { useContext } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step, { StepProps } from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'

import SelectField from '@/components/formik/fields/SelectField'
import OrderStepCard from '@/components/ui/cards/OrderStepCard'
import { SelectOption } from '@/components/ui/inputs/SelectInput'
import RadioGroupField from '@/components/formik/fields/RadioGroupField'

import { BookingFormContext, BookingFormValues } from './BookingForm'
import BottomBar from './BottomBar'
import Summary from './Summary'

type SpecialityStepProps = StepProps & {
  specialityOptions: SelectOption[]
}

export function SpecialityStep(props: SpecialityStepProps) {
  const { active, completed, index = 0, last, specialityOptions, ...rest } = props
  const { isMobile, onComplete, onEditStep, onNextStep, onPreviousStep } = useContext(BookingFormContext)
  const { values } = useFormikContext<BookingFormValues>()
  const { t } = useTranslation()

  const stepProps = {
    active,
    completed,
    index,
    last,
    ...rest,
  }

  if (!active && !completed) {
    return isMobile ? null : (
      <Typography variant="h3" mt={1} mb={1}>
        {t('page.booking.bookingForm.step.speciality.label')}
      </Typography>
    )
  }

  if (isMobile) {
    return (
      <Step {...stepProps}>
        {completed ? null : (
          <StepContent sx={{ height: '100%' }}>
            <Stack height="100%" spacing={2}>
              <Typography variant="h2" mt={1} mb={1}>
                {t('page.booking.bookingForm.step.speciality.label')}
              </Typography>
              <Box flex={1}>
                <Paper sx={{ borderRadius: '6px' }}>
                  <RadioGroupField fullWidth name="speciality" options={specialityOptions} variant="contained" />
                </Paper>
              </Box>
              <BottomBar
                disabled={!values.speciality}
                isFirst={index === 0}
                isLast={last}
                onComplete={onComplete}
                onNext={onNextStep}
                onPrevious={onPreviousStep}
              />
            </Stack>
          </StepContent>
        )}
      </Step>
    )
  }

  return (
    <Step {...stepProps}>
      {completed ? (
        <StepLabel icon={<HealthAndSafetyRoundedIcon />}>
          <Summary label={t('page.booking.bookingForm.step.speciality.label')} onEdit={() => onEditStep(index)} text={values.speciality} />
        </StepLabel>
      ) : (
        <StepContent>
          <OrderStepCard
            disabled={!values.speciality}
            isFirst={index === 0}
            isLast={last}
            title={t('page.booking.bookingForm.step.speciality.label')}
            onComplete={onComplete}
            onNext={onNextStep}
            onPrevious={onPreviousStep}
          >
            <Stack spacing={2} sx={{ p: '10px 30px 35px' }}>
              <SelectField name="speciality" options={specialityOptions} />
            </Stack>
          </OrderStepCard>
        </StepContent>
      )}
    </Step>
  )
}

export default SpecialityStep
